import React from 'react';
import ReactMarkdown from 'react-markdown';
import Link from 'next/link';
import { useRouter } from 'next/router';
import parse from 'html-react-parser';
import { Typography } from '../common';

interface MessageContentProps {
	content: string;
}

const isHTMLContent = (content: string): boolean => {
	return /<\/?[a-z][\s\S]*>/i.test(content);
};

export const MessageContent: React.FC<MessageContentProps> = ({ content }) => {
	const router = useRouter();

	if (isHTMLContent(content)) {
		return <>{parse(content)}</>;
	}

	return (
		<div className="markdown-content break-words min-w-0">
			<ReactMarkdown
				components={{
					h1: ({ children }) => (
						<h1 className="text-xl font-bold mb-2">{children}</h1>
					),
					h2: ({ children }) => (
						<h2 className="text-lg font-bold mb-2">{children}</h2>
					),
					h3: ({ children }) => (
						<h3 className="text-base font-bold mb-1">{children}</h3>
					),
					p: ({ children }) => (
						<Typography type="regular-14" className="mb-2">
							{children}
						</Typography>
					),
					strong: ({ children }) => (
						<strong className="font-bold">{children}</strong>
					),
					em: ({ children }) => <em className="italic">{children}</em>,
					ul: ({ children }) => (
						<ul className="list-disc pl-4 mb-2">{children}</ul>
					),
					ol: ({ children }) => (
						<ol className="list-decimal pl-4 mb-2">{children}</ol>
					),
					li: ({ children }) => <li className="mb-1">{children}</li>,
					code: ({ children }) => (
						<code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">
							{children}
						</code>
					),
					pre: ({ children }) => (
						<pre className="bg-gray-100 p-3 rounded-lg overflow-x-auto mb-2">
							{children}
						</pre>
					),
					blockquote: ({ children }) => (
						<blockquote className="border-l-4 border-gray-300 pl-4 italic mb-2">
							{children}
						</blockquote>
					),
					a: ({ href, children }) => {
						const isInternalLink =
							href && (href.startsWith('/') || href.startsWith('.'));

						if (isInternalLink) {
							if (router.asPath === href) {
								return <span className="text-current">{children}</span>;
							}
							return (
								<Link href={href} className="text-blue-link hover:underline">
									{children}
								</Link>
							);
						}

						return (
							<a
								href={href}
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-link hover:underline"
							>
								{children}
							</a>
						);
					},
				}}
			>
				{content}
			</ReactMarkdown>
		</div>
	);
};
